import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_API_URL } from "../utils/constants";

const CourseDetailsSlice = createSlice({
    name: "CourseDetailsData",
    initialState: {
        loader: false,
        error: null,
        success: null,
        courseList: [],
        allCourses: [],
        inProgressCourses: [],
        completedCourses: [],
        yetToStartCourses: [],
        completedHubsCount: 0,
        totalHubsCount: 0,
        totalBadges: 0,
        hubBadges: {},
        categoryBadges: {},
        moduleBadges: [],
        coursesByHub: [],
        categoryModulesByHub: {}
    },
    reducers: {
        setLoader: (state, action) => {
            state.loader = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setSuccess: (state, action) => {
            state.success = action.payload;
        },
        resetStatus: (state) => {
            state.error = null;
            state.success = null;
        },
        setCourseList: (state, action) => {
            state.courseList = action.payload;
        },
        setAllCourses: (state, action) => {
            state.allCourses = action.payload;
        },
        setInProgressCourses: (state, action) => {
            state.inProgressCourses = action.payload;
        },
        setCompletedCourses: (state, action) => {
            state.completedCourses = action.payload;
        },
        setYetToStartCourses: (state, action) => {
            state.yetToStartCourses = action.payload;
        },
        setCompletedHubsCount: (state, action) => {
            state.completedHubsCount = action.payload;
        },
        setTotalHubsCount: (state, action) => {
            state.totalHubsCount = action.payload;
        },
        setTotalBadges: (state, action) => {
            state.totalBadges = action.payload;
        },
        setHubBadges: (state, action) => {
            state.hubBadges = action.payload;
        },
        setCategoryBadges: (state, action) => {
            state.categoryBadges = action.payload;
        },
        setModuleBadges: (state, action) => {
            state.moduleBadges = action.payload;
        },
        setCoursesByHub: (state, action) => {
            state.coursesByHub = action.payload;
        },
        setCategoryModulesByHub: (state, action) => {
            state.categoryModulesByHub = action.payload;
        }
    }
});

export const {
    setLoader,
    setError,
    setSuccess,
    resetStatus,
    setCourseList,
    setAllCourses,
    setInProgressCourses,
    setCompletedCourses,
    setYetToStartCourses,
    setCompletedHubsCount,
    setTotalHubsCount,
    setTotalBadges,
    setHubBadges,
    setCategoryBadges,
    setModuleBadges,
    setCoursesByHub,
    setCategoryModulesByHub
} = CourseDetailsSlice.actions;

export default CourseDetailsSlice.reducer;

export function uploadCourses(hubName, file) {
    return (dispatch) => {
        dispatch(setLoader(true));
        dispatch(resetStatus());

        const formData = new FormData();
        formData.append("file", file);
        formData.append("hubName", hubName);

        axios.post(
            `${BASE_API_URL}/upload-courses`,
            formData,
            {
                headers: {
                    Authorization: localStorage.getItem("token"),
                    "Content-Type": "multipart/form-data",
                },
            }
        )
            .then((res) => {
                dispatch(setLoader(false));
                dispatch(setSuccess(res.data.message || "Courses uploaded successfully"));
            })
            .catch((err) => {
                dispatch(setLoader(false));
                dispatch(
                    setError(
                        err.response?.data?.message || "Upload failed. Please try again."
                    )
                );
            });
    };
}

export function getCoursesByHub(hubName) {
    return (dispatch) => {
        dispatch(setLoader(true));
        dispatch(setError(null));

        axios.post(
            `${BASE_API_URL}/getCoursesByHub`,
            { hubName },
            {
                headers: {
                    Authorization: localStorage.getItem("token"),
                    "Content-Type": "application/json",
                },
            }
        )
            .then((res) => {
                dispatch(setCourseList(res.data));
                dispatch(setLoader(false));
            })
            .catch(() => {
                dispatch(setError("Failed to load courses"));
                dispatch(setLoader(false));
            });
    };
}

export function getAllCourses() {
    return (dispatch) => {
        dispatch(setLoader(true));
        dispatch(setError(null));

        axios.post(
            `${BASE_API_URL}/getAllCourses`,
            {},
            {
                headers: {
                    Authorization: localStorage.getItem("token"),
                    "Content-Type": "application/json",
                },
            }
        )
            .then((res) => {
                dispatch(setAllCourses(res.data));
                dispatch(setLoader(false));
            })
            .catch((err) => {
                dispatch(
                    setError(
                        err.response?.data?.message || "Failed to load courses"
                    )
                );
                dispatch(setLoader(false));
            });
    };
}

export function getLearnerCourses(enterpriseId) {
    return (dispatch) => {
        dispatch(setLoader(true));
        dispatch(setError(null));

        axios.post(
            `${BASE_API_URL}/getLearnerCourses`,
            { enterpriseId },
            {
                headers: {
                    Authorization: localStorage.getItem("token"),
                    "Content-Type": "application/json"
                }
            }
        )
            .then((res) => {
                const {
                    inProgress,
                    completed,
                    yetToStart,

                    completedHubsCount,
                    totalHubsCount,

                    totalBadges,
                    hubBadges,
                    categoryBadges,
                    moduleBadges,

                    coursesByHub,
                    categoryModules
                } = res.data;

                const categoryModulesByHub = {};

                coursesByHub.forEach((hub) => {
                    const hubName = hub.hubName;
                    categoryModulesByHub[hubName] = {};

                    Object.entries(categoryModules).forEach(([category, modules]) => {
                        const filtered = modules
                            .filter((m) =>
                                hub.courses.some(
                                    (c) =>
                                        c.category === category &&
                                        c.modules === m.moduleName
                                )
                            )
                            .sort((a, b) => a.indexing - b.indexing);

                        if (filtered.length) {
                            categoryModulesByHub[hubName][category] = filtered;
                        }
                    });
                });

                dispatch(setInProgressCourses(inProgress));
                dispatch(setCompletedCourses(completed));
                dispatch(setYetToStartCourses(yetToStart));
                dispatch(setCompletedHubsCount(completedHubsCount));
                dispatch(setTotalHubsCount(totalHubsCount));
                dispatch(setTotalBadges(totalBadges));
                dispatch(setHubBadges(hubBadges));
                dispatch(setCategoryBadges(categoryBadges));
                dispatch(setModuleBadges(moduleBadges));
                dispatch(setCoursesByHub(coursesByHub));
                dispatch(setCategoryModulesByHub(categoryModulesByHub));
                dispatch(setLoader(false));
            })
            .catch((err) => {
                dispatch(
                    setError(
                        err.response?.data?.message || "Failed to load learner courses"
                    )
                );
                dispatch(setLoader(false));
            });
    };
}
